'use client';

import styled from '@emotion/styled';
import GenericPage, { IGenericPageProps } from '../../app/pages/ui/GenericPage';
import { Css, Responsive } from '../../../ui';
import { getCurrentAirportCodeFromId } from '../../../components/common/navigation/utils/getCurrentAirportCode';
import { useApi } from '../../../data/hooks/useApi';
import { PARKING_AVAILABILITY_API } from '../domain/api';
import { IParkingAvailabilityResponse } from '../domain/models/IParkingAvailabilityResponse';
import {
	InfoCardPageContentWrap,
	InfoCardSectionTitle,
} from '../../security/ui/SecurityPage';
import ErrorMessage from '../../shared/components/error/ErrorMessage';
import { ErrorMessages } from '../../../utils/errorMessages';
import ParkingList from './ParkingList';
import RefreshData from '../../../components/common/refresh-data/RefreshData';
import ParkingCardsShimmer from './ParkingCardsShimmer';
import { ParkingBookingWidget } from './ParkingBookingWidget';
import { AirportCodesWithParkingBooking } from '../domain/models/AirportParkingBookingRoute';
import { ISiteSectionEntry } from '../../../contentful/types/ISiteSection';
import { IParkingLocationEntry } from '../../../contentful/types/IParkingLocationEntry';

interface IParkingAvailabilityPageProps extends IGenericPageProps {
	siteSection?: ISiteSectionEntry;
	contentfulParkingLocations?: IParkingLocationEntry[];
}

export default function ParkingAvailabilityPage({
	page,
	siteSection,
	contentfulParkingLocations,
}: IParkingAvailabilityPageProps) {
	const airportCode = getCurrentAirportCodeFromId(
		page.fields.parent?.fields.id,
	);

	const { data, loading, error, refetch } =
		useApi<IParkingAvailabilityResponse>().useGetApi({
			url: `${PARKING_AVAILABILITY_API}/${airportCode}`,
		});

	const parkingData = data?.data.parkingAvailability;

	const renderErrorMessage = () => {
		if (error || !data) {
			return <ErrorMessage message={ErrorMessages.GenericError} />;
		}

		if (parkingData?.length === 0) {
			return (
				<ErrorMessage message={ErrorMessages.SecurityCheckpointsNotFound} />
			);
		}
		return null;
	};

	return (
		<GenericPage page={page} siteSection={siteSection}>
			<InfoCardPageContentWrap>
				<ParkingBookingWidget
					airportCode={airportCode as AirportCodesWithParkingBooking}
				/>
				<InfoCardSectionTitle styling="h4">
					Current Parking Availability
				</InfoCardSectionTitle>
				{loading && !error ? (
					<ParkingCardsShimmer />
				) : error ? (
					renderErrorMessage()
				) : (
					<ParkingList
						parkingOptions={parkingData}
						airportCode={airportCode}
						contentfulParkingLocations={contentfulParkingLocations}
					/>
				)}
				{parkingData?.length !== 0 && (
					<RefreshData label="Refresh Availability" refetch={refetch} />
				)}
			</InfoCardPageContentWrap>
		</GenericPage>
	);
}

export const WidgetWrap = styled.div`
	margin: ${Css.pxToRem(40)} auto;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin: ${Css.pxToRem(24)} auto;
	}
`;
